import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifyError } from '@sharefiledev/flow-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ESignatureClient } from '../../data/eSignatureClient';
import { t } from '../../util';

export const useSignerPage = (signerId: string) => {
	const [isSignerPageDrawerOpen, setIsSignerPageDrawerOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const location = useLocation();
	const searchParams = useRef(
		location.search ? new URLSearchParams(location.search) : new URLSearchParams()
	);
	const queryKey = useMemo(() => ['sessionAuthenticate', signerId], [signerId]);
	const hasTriggeredAuth = useRef(false);
	const queryClient = useQueryClient();
	const {
		isError: sessionAuthenticateErrorFetched,
		isSuccess: sessionAuthenticateDataFetched,
		data: sessionAuthenticateData,
		error: sessionAuthenticateError,
		refetch: refetchSessionAuthenticate,
	} = useQuery(queryKey, () => ESignatureClient.sessionAuthenticate(signerId), {
		enabled: false,
		refetchOnWindowFocus: false,
		retry: (_failureCount, error) => !error,
	});

	const startSignFromDashboard = () => {
		if (searchParams.current.has('access_token')) {
			setIsSignerPageDrawerOpen(true);
			return;
		}

		if (sessionAuthenticateData) {
			searchParams.current.set('access_token', sessionAuthenticateData.access_token);
			setIsSignerPageDrawerOpen(true);
			return;
		}

		hasTriggeredAuth.current = true;
		setIsLoading(true);
		refetchSessionAuthenticate();
	};
	const resetSessionAuthState = useCallback(() => {
		queryClient.removeQueries({ queryKey });
	}, [queryClient, queryKey]);

	useEffect(() => {
		if (
			hasTriggeredAuth.current &&
			(sessionAuthenticateDataFetched || sessionAuthenticateErrorFetched)
		) {
			hasTriggeredAuth.current = false;

			if (sessionAuthenticateErrorFetched) {
				const errorObject = sessionAuthenticateError as any;
				try {
					const errorMessage = JSON.parse(errorObject?.response?.data?.[0]).message;
					notifyError(errorMessage || t('genericError'));
				} catch (e) {
					const fallbackErrorMessage = errorObject?.response?.data?.[0];
					notifyError(fallbackErrorMessage || t('genericError'));
				}
				setIsLoading(false);
				return;
			}

			setIsLoading(false);
			searchParams.current.set('access_token', sessionAuthenticateData.access_token);
			setIsSignerPageDrawerOpen(true);
		}
	}, [
		sessionAuthenticateData,
		sessionAuthenticateError,
		sessionAuthenticateErrorFetched,
		sessionAuthenticateDataFetched,
	]);

	if (!signerId) {
		return {
			isSignerPageDrawerOpen,
			setIsSignerPageDrawerOpen,
			isLoading,
			sessionAuthenticateErrorFetched,
			searchParams: searchParams.current,
		};
	}

	return {
		isSignerPageDrawerOpen,
		setIsSignerPageDrawerOpen,
		isLoading,
		searchParams: searchParams.current,
		sessionAuthenticateErrorFetched,
		startSignFromDashboard,
		resetSessionAuthState,
	};
};
